import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import ChatBubbleOutlineIcon from "@material-ui/icons/ChatBubbleOutline";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import FileCopy from "@material-ui/icons/FileCopy";
import FilterListIcon from "@material-ui/icons/FilterList";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";
import "./ticketPublicoPage.css";

const tChat = (key, opts) =>
  i18n.t(`ticketPanelConfig.ticketPublicPage.chat.${key}`, opts);

const initialsFromName = (name) => {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "TP";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

const defaultOptionName = (n) => tChat("optionFallback", { n });

const localeForDates = () =>
  String(i18n.language || "pt").toLowerCase().startsWith("en")
    ? "en-US"
    : "pt-BR";

const formatListTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString(localeForDates(), {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

const formatMsgTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(localeForDates(), {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "";
  }
};

/** Mesmo formato que PublicPortalService.formatProtocol no backend */
const formatTicketProtocol = (row) => {
  if (row == null || row.id == null || !row.createdAt) return "";
  try {
    const y = new Date(row.createdAt).getFullYear();
    return `#${y}-${String(row.id).padStart(4, "0")}`;
  } catch {
    return "";
  }
};

const parseOpeningCustomFieldsJson = (raw) => {
  if (!raw || typeof raw !== "string" || !raw.trim()) return {};
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
      return parsed;
    }
  } catch {
    /* ignore */
  }
  return {};
};

const activeCustomFields = (customFields) =>
  (customFields || []).filter((f) => String(f.question || "").trim());

const getSubtitleField = (customFields) =>
  activeCustomFields(customFields).find((f) => f.variable === "subtitle");

const USER_SUB_FIELD_SEP = " · ";

const buildUserSubDisplay = (customFields, openingCustomFieldsJson, lastPreview) => {
  const answers = parseOpeningCustomFieldsJson(openingCustomFieldsJson);
  const subtitleField = getSubtitleField(customFields);
  if (subtitleField) {
    const sub = String(answers[subtitleField.id] ?? "").trim();
    if (sub) return sub;
  }
  const active = activeCustomFields(customFields);
  if (active.length > 0) {
    return active
      .map(
        (f) =>
          `${String(f.question).trim()}: ${String(answers[f.id] ?? "").trim()}`
      )
      .join(USER_SUB_FIELD_SEP);
  }
  return lastPreview != null && lastPreview !== ""
    ? String(lastPreview).replace(/\r?\n/g, " ").trim()
    : "";
};

const buildUserSubAnswersToSave = (
  userSubDraft,
  customFields,
  openingCustomFieldsJson
) => {
  const existing = parseOpeningCustomFieldsJson(openingCustomFieldsJson);
  const text = String(userSubDraft || "").trim();
  const subtitleField = getSubtitleField(customFields);
  if (subtitleField) {
    return { ...existing, [subtitleField.id]: text };
  }
  const active = activeCustomFields(customFields);
  if (active.length > 0) {
    const parsed = parseUserSubTextToAnswers(text, customFields);
    return { ...existing, ...parsed };
  }
  return null;
};

const parseUserSubTextToAnswers = (text, customFields) => {
  const active = activeCustomFields(customFields);
  const answers = {};
  const lines = String(text || "")
    .split(/\r?\n| · /)
    .map((line) => line.trim())
    .filter(Boolean);
  for (const field of active) {
    const q = String(field.question).trim();
    const prefix = `${q}:`;
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed || trimmed === "---") continue;
      if (trimmed.startsWith(prefix)) {
        answers[field.id] = trimmed.slice(prefix.length).trim();
        break;
      }
    }
  }
  return answers;
};

const answersEqual = (a, b) => JSON.stringify(a || {}) === JSON.stringify(b || {});

const DEFAULT_LIST_FILTERS = {
  situation: ["in_progress"],
  col1: [],
  col2: [],
  col3: [],
};

const SITUATION_FILTER_OPTIONS = [
  { id: "in_progress", labelKey: "situationInProgress" },
  { id: "archived", labelKey: "situationArchived" },
];

const ticketColStageValue = (ticket, colKey) => {
  if (colKey === "1" || colKey === 1) {
    return String(
      ticket?.supportKanbanStageId || ticket?.classificationOption1 || ""
    ).trim();
  }
  const n = String(colKey);
  return String(ticket?.[`classificationOption${n}`] || "").trim();
};

const toggleFilterValue = (list, value) => {
  const set = new Set(Array.isArray(list) ? list : []);
  if (set.has(value)) set.delete(value);
  else set.add(value);
  return [...set];
};

const filtersAreEqual = (a, b) =>
  JSON.stringify(a || {}) === JSON.stringify(b || {});

const mapDetailToState = (data) => ({
  classif: {
    /* Opção 1 = coluna do Kanban Suporte: o arrasto só persiste supportKanbanStageId */
    1: data.supportKanbanStageId || data.classificationOption1 || "",
    2: data.classificationOption2 || "",
    3: data.classificationOption3 || "",
  },
  phones: (data.phones || []).map((p) => ({
    id: p.id,
    phone: p.phone,
  })),
  internalNotes: (data.internalNotes || []).map((n) => ({
    id: n.id,
    text: n.body,
    date: formatMsgTime(n.createdAt),
  })),
  messages: data.messages || [],
});

const TicketPublicoPage = () => {
  const history = useHistory();
  const location = useLocation();
  const { user } = useContext(AuthContext);
  const fileInputRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const prevAdminMsgDigestRef = useRef("");
  const selectedIdRef = useRef(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [optionNames, setOptionNames] = useState({
    1: "",
    2: "",
    3: "",
  });
  const [stageOptions, setStageOptions] = useState({
    1: [],
    2: [],
    3: [],
  });
  const [customFields, setCustomFields] = useState([]);

  const [listLoading, setListLoading] = useState(true);
  const [ticketsList, setTicketsList] = useState([]);
  const [listFilters, setListFilters] = useState({ ...DEFAULT_LIST_FILTERS });
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [filterDraft, setFilterDraft] = useState({ ...DEFAULT_LIST_FILTERS });
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const [messageDraft, setMessageDraft] = useState("");
  const [contactDraft, setContactDraft] = useState("");
  const [classif, setClassif] = useState({ 1: "", 2: "", 3: "" });
  const [phones, setPhones] = useState([]);
  const [internalDraft, setInternalDraft] = useState("");
  const [internalNotes, setInternalNotes] = useState([]);
  const [messages, setMessages] = useState([]);
  const [pendingFile, setPendingFile] = useState(null);
  const [creating, setCreating] = useState(false);
  const [contactNameDraft, setContactNameDraft] = useState("");
  const [userSubDraft, setUserSubDraft] = useState("");
  const [messageMenu, setMessageMenu] = useState(null);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [editMessageDraft, setEditMessageDraft] = useState("");
  const prevSelectedIdRef = useRef(null);
  const [attachmentPreview, setAttachmentPreview] = useState({
    open: false,
    loading: false,
    messageId: null,
    url: "",
    filename: "",
    kind: "file", // "pdf" | "image" | "video" | "file"
  });
  const attachmentPreviewUrlRef = useRef("");

  const guessAttachmentKind = (contentType, filename) => {
    const ct = String(contentType || "").toLowerCase();
    const name = String(filename || "").toLowerCase();
    const ext = name.includes(".") ? name.split(".").pop() : "";

    if (ct.includes("pdf") || ext === "pdf") return "pdf";
    if (ct.startsWith("image/") || ["png", "jpg", "jpeg", "gif", "webp", "bmp", "svg"].includes(ext)) {
      return "image";
    }
    if (ct.startsWith("video/") || ["mp4", "webm", "ogg", "mov", "m4v"].includes(ext)) return "video";
    return "file";
  };

  const closeAttachmentPreview = useCallback(() => {
    setAttachmentPreview((s) => ({ ...s, open: false, loading: false }));
    if (attachmentPreviewUrlRef.current) {
      try {
        URL.revokeObjectURL(attachmentPreviewUrlRef.current);
      } catch {
        /* ignore */
      }
      attachmentPreviewUrlRef.current = "";
    }
    setTimeout(() => {
      setAttachmentPreview((s) => ({
        ...s,
        messageId: null,
        url: "",
        filename: "",
        kind: "file",
      }));
    }, 0);
  }, []);

  useEffect(() => {
    if (!filterModalOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") setFilterModalOpen(false);
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [filterModalOpen]);

  useEffect(() => {
    if (!attachmentPreview.open) return undefined;
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeAttachmentPreview();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [attachmentPreview.open, closeAttachmentPreview]);

  const loadConfig = useCallback(async () => {
    setConfigLoading(true);
    try {
      const { data } = await api.get("/ticket-panel-config");
      const on = data?.optionNames && typeof data.optionNames === "object"
        ? data.optionNames
        : {};
      setOptionNames({
        1: String(on["1"] ?? on[1] ?? ""),
        2: String(on["2"] ?? on[2] ?? ""),
        3: String(on["3"] ?? on[3] ?? ""),
      });
      const st = data?.stages && typeof data.stages === "object" ? data.stages : {};
      const col = (k) =>
        (Array.isArray(st[k]) ? st[k] : []).map((s) => ({
          id: String(s?.id ?? ""),
          name: String(s?.name ?? "").trim() || "—",
        }));
      setStageOptions({ 1: col("1"), 2: col("2"), 3: col("3") });
      const cf = Array.isArray(data?.customFields) ? data.customFields : [];
      setCustomFields(
        cf.map((f) => ({
          id: String(f?.id ?? ""),
          question: String(f?.question ?? ""),
          type: String(f?.type ?? "short"),
          variable: String(f?.variable ?? "none"),
        }))
      );
    } catch (err) {
      toastError(err);
    } finally {
      setConfigLoading(false);
    }
  }, []);

  const loadList = useCallback(async () => {
    setListLoading(true);
    try {
      const { data } = await api.get("/public-panel-tickets");
      setTicketsList(Array.isArray(data) ? data : []);
    } catch (err) {
      toastError(err);
      setTicketsList([]);
    } finally {
      setListLoading(false);
    }
  }, []);

  const applyDetail = useCallback((data) => {
    if (!data) return;
    setDetail(data);
    const m = mapDetailToState(data);
    setClassif(m.classif);
    setPhones(m.phones);
    setInternalNotes(m.internalNotes);
    setMessages(m.messages);
  }, []);

  const loadDetail = useCallback(
    async (id) => {
      if (id == null) return;
      setDetailLoading(true);
      try {
        const { data } = await api.get(`/public-panel-tickets/${id}`);
        applyDetail(data);
      } catch (err) {
        toastError(err);
        setSelectedId(null);
        setDetail(null);
      } finally {
        setDetailLoading(false);
      }
    },
    [applyDetail]
  );

  const loadDetailQuiet = useCallback(
    async (id) => {
      if (id == null) return;
      try {
        const { data } = await api.get(`/public-panel-tickets/${id}`);
        if (selectedIdRef.current !== id) return;
        applyDetail(data);
      } catch {
        /* polling: ignora erros transitórios */
      }
    },
    [applyDetail]
  );

  useEffect(() => {
    selectedIdRef.current = selectedId;
  }, [selectedId]);

  useEffect(() => {
    loadConfig();
    loadList();
  }, [loadConfig, loadList]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get("ticket");
    if (raw == null || String(raw).trim() === "") return;
    const id = parseInt(String(raw).trim(), 10);
    if (!Number.isFinite(id) || id <= 0) return;
    setSelectedId(id);
    params.delete("ticket");
    const qs = params.toString();
    history.replace(`${location.pathname}${qs ? `?${qs}` : ""}`);
  }, [location.search, location.pathname, history]);

  useEffect(() => {
    if (selectedId != null) {
      loadDetail(selectedId);
    } else {
      setDetail(null);
      setClassif({ 1: "", 2: "", 3: "" });
      setPhones([]);
      setInternalNotes([]);
      setMessages([]);
      setMessageDraft("");
      setContactDraft("");
      setInternalDraft("");
      setPendingFile(null);
      setContactNameDraft("");
      setUserSubDraft("");
    }
  }, [selectedId, loadDetail]);

  useEffect(() => {
    if (selectedId == null) return undefined;
    const tick = () => loadDetailQuiet(selectedId);
    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [selectedId, loadDetailQuiet]);

  useEffect(() => {
    if (!messageMenu) return undefined;
    const close = () => setMessageMenu(null);
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };
    window.addEventListener("click", close);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [messageMenu]);

  useEffect(() => {
    setMessageMenu(null);
    setEditingMessageId(null);
    setEditMessageDraft("");
  }, [selectedId]);

  useEffect(() => {
    const el = chatMessagesRef.current;
    if (selectedId == null || !el) return;
    if (!messages.length) {
      el.scrollTop = 0;
      return;
    }
    const last = messages[messages.length - 1];
    const digest = `${messages.length}:${last?.id}:${last?.createdAt}`;
    if (digest === prevAdminMsgDigestRef.current) return;
    prevAdminMsgDigestRef.current = digest;
    requestAnimationFrame(() => {
      el.scrollTo({
        top: el.scrollHeight,
        behavior: "smooth",
      });
    });
  }, [messages, selectedId]);

  useEffect(() => {
    if (selectedId !== prevSelectedIdRef.current) {
      prevSelectedIdRef.current = selectedId;
      if (selectedId == null) {
        setContactNameDraft("");
        setUserSubDraft("");
      } else {
        const row = ticketsList.find((x) => x.id === selectedId);
        setContactNameDraft(String(row?.contactName ?? ""));
        setUserSubDraft(
          buildUserSubDisplay(
            customFields,
            row?.openingCustomFieldsJson,
            row?.lastPreview
          )
        );
      }
    }
  }, [selectedId, ticketsList, customFields]);

  useEffect(() => {
    if (selectedId == null || listLoading || detailLoading) return;
    if (!ticketsList.some((x) => x.id === selectedId)) {
      setSelectedId(null);
    }
  }, [listLoading, detailLoading, ticketsList, selectedId]);

  useEffect(() => {
    if (selectedId == null || detail?.id !== selectedId || configLoading) return;
    setContactNameDraft(String(detail.contactName ?? ""));
    setUserSubDraft(
      buildUserSubDisplay(
        customFields,
        detail.openingCustomFieldsJson,
        detail.lastPreview
      )
    );
  }, [
    selectedId,
    detail?.id,
    detail?.contactName,
    detail?.openingCustomFieldsJson,
    detail?.lastPreview,
    customFields,
    configLoading,
  ]);

  const filteredTickets = useMemo(() => {
    const q = search.trim().toLowerCase();
    const qNoHash = q.replace(/^#/, "");
    return ticketsList.filter((x) => {
      if (listFilters.situation.length > 0) {
        const sit = x.situation === "archived" ? "archived" : "in_progress";
        if (!listFilters.situation.includes(sit)) return false;
      }
      for (const colKey of ["1", "2", "3"]) {
        const selected = listFilters[`col${colKey}`];
        if (!selected?.length) continue;
        const value = ticketColStageValue(x, colKey);
        if (!value || !selected.includes(value)) return false;
      }
      if (!q) return true;
      const protocol = String(formatTicketProtocol(x) || "").toLowerCase();
      return (
        String(x.contactName || "")
          .toLowerCase()
          .includes(q) ||
        String(x.lastPreview || "")
          .toLowerCase()
          .includes(q) ||
        protocol.includes(q) ||
        protocol.includes(qNoHash) ||
        String(x.id ?? "").includes(qNoHash)
      );
    });
  }, [search, ticketsList, listFilters]);

  const hasActiveListFilters = useMemo(() => {
    if (listFilters.col1.length || listFilters.col2.length || listFilters.col3.length) {
      return true;
    }
    return !filtersAreEqual(
      { situation: [...listFilters.situation].sort() },
      { situation: [...DEFAULT_LIST_FILTERS.situation].sort() }
    );
  }, [listFilters]);

  const openFilterModal = () => {
    setFilterDraft({
      situation: [...listFilters.situation],
      col1: [...listFilters.col1],
      col2: [...listFilters.col2],
      col3: [...listFilters.col3],
    });
    setFilterModalOpen(true);
  };

  const applyListFilters = () => {
    setListFilters({
      situation: [...filterDraft.situation],
      col1: [...filterDraft.col1],
      col2: [...filterDraft.col2],
      col3: [...filterDraft.col3],
    });
    setFilterModalOpen(false);
  };

  const clearFilterDraft = () => {
    setFilterDraft({ ...DEFAULT_LIST_FILTERS, situation: [], col1: [], col2: [], col3: [] });
  };

  const renderFilterStageGroup = (colKey) => {
    const stages = stageOptions[colKey] || [];
    const label =
      (optionNames[colKey] || "").trim() || defaultOptionName(colKey);
    const selected = filterDraft[`col${colKey}`] || [];
    if (!stages.length) return null;
    return (
      <div key={colKey} className="tp-filter-group">
        <div className="tp-filter-group-title">{label}</div>
        <div className="tp-filter-check-list">
          {stages.map((stage) => {
            const sid = String(stage.id || "");
            if (!sid) return null;
            const checked = selected.includes(sid);
            return (
              <label key={sid} className="tp-filter-check-item">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() =>
                    setFilterDraft((prev) => ({
                      ...prev,
                      [`col${colKey}`]: toggleFilterValue(prev[`col${colKey}`], sid),
                    }))
                  }
                />
                <span>{stage.name}</span>
              </label>
            );
          })}
        </div>
      </div>
    );
  };

  const selected = useMemo(() => {
    if (selectedId == null) return null;
    return ticketsList.find((x) => x.id === selectedId) || detail;
  }, [selectedId, ticketsList, detail]);

  const patchClassifications = async (colKey, value) => {
    if (selectedId == null) return;
    try {
      const payload = {
        [`classificationOption${colKey}`]: value || null,
      };
      // Opção 1 = mesma coluna do Kanban Suporte; precisa atualizar o estágio para disparar WA (notifySupportKanbanStage).
      if (colKey === 1) {
        payload.supportKanbanStageId = value || null;
      }
      const { data } = await api.patch(`/public-panel-tickets/${selectedId}`, payload);
      applyDetail(data);
      await loadList();
    } catch (err) {
      toastError(err);
    }
  };

  const patchSituation = async (value) => {
    if (selectedId == null) return;
    try {
      const { data } = await api.patch(`/public-panel-tickets/${selectedId}`, {
        situation: value,
      });
      applyDetail(data);
      await loadList();
    } catch (err) {
      toastError(err);
    }
  };

  const patchPhonesList = async (nextPhones) => {
    if (selectedId == null) return;
    try {
      const { data } = await api.patch(`/public-panel-tickets/${selectedId}`, {
        phones: nextPhones,
      });
      applyDetail(data);
      await loadList();
    } catch (err) {
      toastError(err);
    }
  };

  const saveContactName = async () => {
    if (selectedId == null || detailLoading || !detail || detail.id !== selectedId) {
      return;
    }
    const next = contactNameDraft.trim() || tChat("defaultTicketName");
    const current =
      String(detail.contactName ?? "").trim() || tChat("defaultTicketName");
    if (next === current) return;
    try {
      const { data } = await api.patch(`/public-panel-tickets/${selectedId}`, {
        contactName: next,
      });
      applyDetail(data);
      setContactNameDraft(data.contactName || next);
      await loadList();
    } catch (err) {
      toastError(err);
      setContactNameDraft(String(detail?.contactName ?? ""));
    }
  };

  const saveUserSub = async () => {
    if (selectedId == null || detailLoading || !detail || detail.id !== selectedId) {
      return;
    }
    const hasCustomFields = activeCustomFields(customFields).length > 0;
    try {
      if (hasCustomFields) {
        const nextAnswers = buildUserSubAnswersToSave(
          userSubDraft,
          customFields,
          detail.openingCustomFieldsJson
        );
        const currentAnswers = parseOpeningCustomFieldsJson(
          detail.openingCustomFieldsJson
        );
        if (answersEqual(nextAnswers, currentAnswers)) return;
        const { data } = await api.patch(`/public-panel-tickets/${selectedId}`, {
          openingCustomFields: nextAnswers,
        });
        applyDetail(data);
        setUserSubDraft(
          buildUserSubDisplay(
            customFields,
            data.openingCustomFieldsJson,
            data.lastPreview
          )
        );
      } else {
        const next = userSubDraft.trim();
        const current = String(detail.lastPreview ?? "").trim();
        if (next === current) return;
        const { data } = await api.patch(`/public-panel-tickets/${selectedId}`, {
          lastPreview: next || null,
        });
        applyDetail(data);
        setUserSubDraft(buildUserSubDisplay(customFields, null, data.lastPreview));
      }
      await loadList();
    } catch (err) {
      toastError(err);
      setUserSubDraft(
        buildUserSubDisplay(
          customFields,
          detail.openingCustomFieldsJson,
          detail.lastPreview
        )
      );
    }
  };

  const handleSelectTicket = (id) => {
    setSelectedId(id);
  };

  const handleCreateTicket = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/public-panel-tickets", {});
      await loadList();
      if (data?.id) {
        setSelectedId(data.id);
        toast.success(tChat("createdOk"));
      }
    } catch (err) {
      toastError(err);
    } finally {
      setCreating(false);
    }
  };

  const addContact = async () => {
    const v = contactDraft.trim();
    if (!v || selectedId == null) return;
    const next = [...phones.map((p) => p.phone), v];
    setContactDraft("");
    await patchPhonesList(next);
  };

  const removeContact = async (idx) => {
    const next = phones.map((p) => p.phone).filter((_, i) => i !== idx);
    await patchPhonesList(next);
  };

  const addInternalNote = async () => {
    const text = internalDraft.trim();
    if (!text || selectedId == null) return;
    try {
      const { data } = await api.post(
        `/public-panel-tickets/${selectedId}/internal-notes`,
        { body: text }
      );
      applyDetail(data);
      setInternalDraft("");
      await loadList();
      toast.success(tChat("noteSaved"));
    } catch (err) {
      toastError(err);
    }
  };

  const removeNote = async (noteId) => {
    if (selectedId == null) return;
    try {
      const { data } = await api.delete(
        `/public-panel-tickets/${selectedId}/internal-notes/${noteId}`
      );
      applyDetail(data);
      await loadList();
    } catch (err) {
      toastError(err);
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files && e.target.files[0];
    if (f) setPendingFile({ file: f, name: f.name });
    e.target.value = "";
  };

  const downloadMessageAttachment = async (messageId, filename) => {
    if (selectedId == null) return;
    try {
      const res = await api.get(
        `/public-panel-tickets/${selectedId}/messages/${messageId}/attachment`,
        { responseType: "blob" }
      );
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename || "anexo";
      a.rel = "noopener noreferrer";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      toastError(err);
    }
  };

  const openAttachmentPreview = async (messageId, filename) => {
    if (selectedId == null) return;
    setAttachmentPreview((s) => ({
      ...s,
      open: true,
      loading: true,
      messageId,
      url: "",
      filename: filename || "anexo",
      kind: "file",
    }));
    try {
      const res = await api.get(
        `/public-panel-tickets/${selectedId}/messages/${messageId}/attachment`,
        { responseType: "blob" }
      );
      const blob = res.data;
      const url = URL.createObjectURL(blob);
      if (attachmentPreviewUrlRef.current) {
        try {
          URL.revokeObjectURL(attachmentPreviewUrlRef.current);
        } catch {
          /* ignore */
        }
      }
      attachmentPreviewUrlRef.current = url;
      const kind = guessAttachmentKind(res?.headers?.["content-type"], filename);
      setAttachmentPreview((s) => ({
        ...s,
        open: true,
        loading: false,
        messageId,
        url,
        filename: filename || "anexo",
        kind,
      }));
    } catch (err) {
      setAttachmentPreview((s) => ({ ...s, loading: false }));
      toastError(err);
    }
  };

  const openMessageContextMenu = (e, msg) => {
    if (msg.direction !== "out") return;
    e.preventDefault();
    e.stopPropagation();
    setMessageMenu({
      x: e.clientX,
      y: e.clientY,
      message: msg,
    });
  };

  const startEditMessage = (msg) => {
    setMessageMenu(null);
    setEditingMessageId(msg.id);
    setEditMessageDraft(String(msg.body || ""));
  };

  const cancelEditMessage = () => {
    setEditingMessageId(null);
    setEditMessageDraft("");
  };

  const saveEditMessage = async () => {
    if (selectedId == null || editingMessageId == null) return;
    const next = editMessageDraft.trim();
    const current = messages.find((m) => m.id === editingMessageId);
    if (!current) {
      cancelEditMessage();
      return;
    }
    const hasAttachment =
      Boolean(current.attachmentStorageKey) ||
      Boolean(current.attachmentName && String(current.attachmentName).trim());
    if (!next && !hasAttachment) {
      toast.error(tChat("messageEditEmpty"));
      return;
    }
    if (next === String(current.body || "").trim()) {
      cancelEditMessage();
      return;
    }
    try {
      const { data } = await api.patch(
        `/public-panel-tickets/${selectedId}/messages/${editingMessageId}`,
        { body: next }
      );
      applyDetail(data);
      cancelEditMessage();
      await loadList();
      toast.success(tChat("messageEdited"));
    } catch (err) {
      toastError(err);
    }
  };

  const deleteMessage = async (msg) => {
    if (selectedId == null || !msg?.id) return;
    setMessageMenu(null);
    if (!window.confirm(tChat("messageDeleteConfirm"))) return;
    try {
      const { data } = await api.delete(
        `/public-panel-tickets/${selectedId}/messages/${msg.id}`
      );
      applyDetail(data);
      if (editingMessageId === msg.id) {
        cancelEditMessage();
      }
      await loadList();
      toast.success(tChat("messageDeleted"));
    } catch (err) {
      toastError(err);
    }
  };

  const sendMessage = async () => {
    if (selectedId == null) return;
    const text = messageDraft.trim();
    const hasFile = Boolean(pendingFile?.file);
    const attachNameOnly = !hasFile && pendingFile?.name ? pendingFile.name : null;
    if (!text && !hasFile && !attachNameOnly) return;
    try {
      let data;
      if (hasFile) {
        const formData = new FormData();
        formData.append("body", text);
        formData.append("direction", "out");
        formData.append("file", pendingFile.file);
        const res = await api.post(
          `/public-panel-tickets/${selectedId}/messages`,
          formData
        );
        data = res.data;
      } else {
        const res = await api.post(
          `/public-panel-tickets/${selectedId}/messages`,
          {
            body: text,
            direction: "out",
            attachmentName: attachNameOnly,
          }
        );
        data = res.data;
      }
      applyDetail(data);
      setMessageDraft("");
      setPendingFile(null);
      await loadList();
    } catch (err) {
      toastError(err);
    }
  };

  const renderSelect = (colKey) => {
    const stages = stageOptions[colKey];
    const label =
      (optionNames[colKey] || "").trim() ||
      defaultOptionName(colKey);
    return (
      <div key={colKey} className="side-field">
        <label htmlFor={`ticket-opt-${colKey}`}>{label}</label>
        <select
          id={`ticket-opt-${colKey}`}
          value={classif[colKey]}
          onChange={(e) => {
            const v = e.target.value;
            setClassif((s) => ({ ...s, [colKey]: v }));
            patchClassifications(colKey, v);
          }}
        >
          <option value="">{tChat("selectNone")}</option>
          {stages.map((s) => (
            <option key={s.id || s.name} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
    );
  };

  const publicPortalFullUrl = useMemo(() => {
    const id = user?.companyId;
    if (id == null || typeof window === "undefined") return "";
    const origin = String(window.location.origin || "").replace(/\/$/, "");
    return `${origin}/portal/${encodeURIComponent(String(id))}`;
  }, [user?.companyId]);

  const copyPublicPortalUrl = useCallback(() => {
    if (!publicPortalFullUrl) return;
    navigator.clipboard.writeText(publicPortalFullUrl).then(
      () => toast.success(tChat("publicPortalLinkCopied")),
      () => toast.error(tChat("publicPortalLinkCopyError"))
    );
  }, [publicPortalFullUrl]);

  return (
    <div className="ticket-publico-chat-root">
      <section id="novo-ticket-publico" className="page active">
        <div className="ticket-public-page">
          <div
            className={`ticket-layout${
              selected ? "" : " ticket-layout--no-details"
            }`}
          >
            <aside className="ticket-left">
              <div className="ticket-left-header">
                <div className="ticket-left-header-row">
                  <h3>{tChat("leftTitle")}</h3>
                  <button
                    type="button"
                    className={`ticket-filter-btn${
                      hasActiveListFilters ? " has-active-filters" : ""
                    }`}
                    onClick={openFilterModal}
                    title={tChat("filterTitle")}
                    aria-label={tChat("filterTitle")}
                  >
                    <FilterListIcon style={{ fontSize: 20 }} />
                  </button>
                </div>
                <p>{tChat("leftSubtitle")}</p>
              </div>
              <div className="ticket-search">
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={tChat("searchPlaceholder")}
                  autoComplete="off"
                />
              </div>
              <div className="ticket-list" id="ticketList">
                {listLoading ? (
                  <div className="rename-note" style={{ padding: 16 }}>
                    {tChat("listLoading")}
                  </div>
                ) : filteredTickets.length === 0 ? (
                  <div className="rename-note" style={{ padding: 16 }}>
                    {ticketsList.length > 0 &&
                    (search.trim() || hasActiveListFilters)
                      ? tChat("listEmptySearch")
                      : tChat("listEmptyForSituation")}
                  </div>
                ) : (
                  filteredTickets.map((item) => (
                    <div
                      key={item.id}
                      role="button"
                      tabIndex={0}
                      className={`ticket-item${
                        selectedId === item.id ? " active" : ""
                      }`}
                      onClick={() => handleSelectTicket(item.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSelectTicket(item.id);
                        }
                      }}
                    >
                      <div className="ticket-item-top">
                        <div className="ticket-user">{item.contactName}</div>
                        <div className="ticket-time">
                          {formatListTime(item.updatedAt)}
                        </div>
                      </div>
                      <div className="ticket-preview">
                        {item.lastPreview || tChat("noPreview")}
                      </div>
                    </div>
                  ))
                )}
              </div>
              <div className="ticket-new-row">
                <button
                  type="button"
                  className="btn-new-ticket"
                  onClick={handleCreateTicket}
                  disabled={creating || listLoading}
                >
                  {creating ? tChat("creating") : tChat("newTicket")}
                </button>
              </div>
            </aside>

            <section className="ticket-main">
              <div className="chat-header">
                <div className="chat-header-left">
                  <div className="chat-avatar" id="chatAvatar">
                    {selected
                      ? initialsFromName(contactNameDraft)
                      : "TP"}
                  </div>
                  <div className="chat-user-wrap">
                    {selected ? (
                      <input
                        type="text"
                        id="chatUserName"
                        className="chat-user-name-input"
                        value={contactNameDraft}
                        onChange={(e) => setContactNameDraft(e.target.value)}
                        onBlur={saveContactName}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            e.target.blur();
                          }
                        }}
                        placeholder={tChat("ticketNamePlaceholder")}
                        disabled={detailLoading}
                        maxLength={255}
                        aria-label={tChat("ticketNameAria")}
                      />
                    ) : (
                      <div className="chat-user-name" id="chatUserName">
                        {tChat("selectTicketTitle")}
                      </div>
                    )}
                    {selected ? (
                      <input
                        type="text"
                        id="chatUserSub"
                        className="chat-user-sub-input"
                        value={userSubDraft}
                        onChange={(e) => setUserSubDraft(e.target.value)}
                        onBlur={saveUserSub}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            e.target.blur();
                          }
                        }}
                        placeholder={tChat("userSubPlaceholder")}
                        disabled={detailLoading}
                        maxLength={500}
                        aria-label={tChat("userSubAria")}
                      />
                    ) : (
                      <div className="chat-user-sub" id="chatUserSub">
                        {tChat("userSubEmpty")}
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className={`chat-status-badge${
                    selected
                      ? detail?.situation === "archived"
                        ? " archived"
                        : " active"
                      : " waiting"
                  }`}
                  id="chatStatusBadge"
                >
                  {selected
                    ? detail?.situation === "archived"
                      ? tChat("statusArchived")
                      : tChat("statusOpen")
                    : tChat("statusWaiting")}
                </div>
              </div>

              <div
                ref={chatMessagesRef}
                className="chat-messages"
                id="chatMessages"
              >
                {!selected ? (
                  <div className="empty-chat">
                    <ChatBubbleOutlineIcon className="empty-chat-icon" />
                    <div>
                      <strong>{tChat("emptyChatTitle")}</strong>
                    </div>
                    <div>{tChat("emptyChatHint")}</div>
                  </div>
                ) : detailLoading ? (
                  <div className="rename-note" style={{ margin: "auto" }}>
                    {tChat("detailLoading")}
                  </div>
                ) : messages.length === 0 ? (
                  <div className="thread-placeholder">
                    {tChat("threadPlaceholder")}
                  </div>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`msg-row ${msg.direction === "in" ? "in" : "out"}${
                        msg.direction === "out" ? " msg-row--staff" : ""
                      }`}
                      onContextMenu={
                        msg.direction === "out"
                          ? (e) => openMessageContextMenu(e, msg)
                          : undefined
                      }
                    >
                      <div className="msg-bubble">
                        {editingMessageId === msg.id ? (
                          <div className="msg-edit-wrap">
                            <textarea
                              className="msg-edit-input"
                              value={editMessageDraft}
                              onChange={(e) => setEditMessageDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                  e.preventDefault();
                                  saveEditMessage();
                                }
                                if (e.key === "Escape") {
                                  e.preventDefault();
                                  cancelEditMessage();
                                }
                              }}
                              autoFocus
                              rows={3}
                            />
                            <div className="msg-edit-actions">
                              <button
                                type="button"
                                className="msg-edit-btn msg-edit-btn--save"
                                onClick={saveEditMessage}
                              >
                                {tChat("messageEditSave")}
                              </button>
                              <button
                                type="button"
                                className="msg-edit-btn"
                                onClick={cancelEditMessage}
                              >
                                {tChat("messageEditCancel")}
                              </button>
                            </div>
                          </div>
                        ) : msg.body ? (
                          <div className="msg-text">{msg.body}</div>
                        ) : null}
                        {msg.attachmentStorageKey && msg.attachmentName ? (
                          <button
                            type="button"
                            className="msg-attach-link"
                            onClick={() =>
                              openAttachmentPreview(
                                msg.id,
                                msg.attachmentName
                              )
                            }
                          >
                            📎 {msg.attachmentName}
                          </button>
                        ) : null}
                        <div className="msg-meta">
                          {formatMsgTime(msg.createdAt)}
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <div className="chat-compose">
                {pendingFile ? (
                  <div className="attachment-preview-bar">
                    <span className="attachment-chip">
                      {pendingFile.name}
                      <button
                        type="button"
                        onClick={() => setPendingFile(null)}
                        aria-label="Remover"
                      >
                        ×
                      </button>
                    </span>
                  </div>
                ) : null}
                <div className="compose-row">
                  <textarea
                    id="msgInput"
                    placeholder={tChat("msgPlaceholder")}
                    value={messageDraft}
                    onChange={(e) => setMessageDraft(e.target.value)}
                    disabled={!selected || detailLoading}
                  />
                  <div className="compose-actions">
                    <button
                      type="button"
                      className="icon-btn"
                      title={tChat("attachTitle")}
                      disabled={!selected || detailLoading}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <AttachFileIcon style={{ fontSize: 20 }} />
                    </button>
                    <input
                      ref={fileInputRef}
                      type="file"
                      id="attachmentInput"
                      style={{ display: "none" }}
                      onChange={onPickFile}
                    />
                    <button
                      type="button"
                      className="send-btn"
                      disabled={!selected || detailLoading}
                      onClick={sendMessage}
                    >
                      {tChat("send")}
                    </button>
                  </div>
                </div>
              </div>
            </section>

            {selected ? (
              <aside className="ticket-right">
                <div className="ticket-right-header">
                  <h3>{tChat("detailsTitle")}</h3>
                  {formatTicketProtocol(selected) ? (
                    <p className="ticket-right-protocol">
                      <span className="ticket-right-protocol-label">
                        {tChat("ticketCode")}
                      </span>
                      <span className="ticket-right-protocol-value">
                        {formatTicketProtocol(selected)}
                      </span>
                    </p>
                  ) : null}
                  <p>{tChat("detailsSubtitle")}</p>
                  {publicPortalFullUrl ? (
                    <div className="ticket-public-portal-box">
                      <div className="ticket-public-portal-label">
                        {tChat("publicPortalUrlLabel")}
                      </div>
                      <div className="ticket-public-portal-row">
                        <input
                          type="text"
                          readOnly
                          className="ticket-public-portal-input"
                          value={publicPortalFullUrl}
                          aria-label={tChat("publicPortalUrlLabel")}
                        />
                        <button
                          type="button"
                          className="ticket-public-portal-copy"
                          onClick={copyPublicPortalUrl}
                          title={tChat("publicPortalCopyTitle")}
                        >
                          <FileCopy style={{ fontSize: 18 }} />
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
                <div className="ticket-right-body">
                  <div className="side-card">
                    <h4>{tChat("contactsTitle")}</h4>
                    <div className="contact-add-row">
                      <input
                        type="text"
                        id="contactInput"
                        placeholder={tChat("contactPlaceholder")}
                        value={contactDraft}
                        onChange={(e) => setContactDraft(e.target.value)}
                        disabled={detailLoading}
                      />
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={addContact}
                        disabled={detailLoading}
                      >
                        +
                      </button>
                    </div>
                    <div className="contact-list" id="contactList">
                      {phones.length === 0 ? (
                        <div className="rename-note">{tChat("noContacts")}</div>
                      ) : (
                        phones.map((row, idx) => (
                          <div key={row.id || idx} className="contact-item">
                            <span>{row.phone}</span>
                            <button
                              type="button"
                              onClick={() => removeContact(idx)}
                              aria-label="Remover"
                            >
                              ×
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="side-card">
                    <h4>{tChat("classificationsTitle")}</h4>
                    <div className="side-field">
                      <label htmlFor="ticketSituationSelect">
                        {tChat("situationTitle")}
                      </label>
                      <select
                        id="ticketSituationSelect"
                        value={
                          detail?.situation === "archived"
                            ? "archived"
                            : "in_progress"
                        }
                        onChange={(e) => patchSituation(e.target.value)}
                        disabled={detailLoading}
                      >
                        <option value="in_progress">
                          {tChat("situationInProgress")}
                        </option>
                        <option value="archived">
                          {tChat("situationArchived")}
                        </option>
                      </select>
                    </div>
                    {configLoading ? (
                      <div className="rename-note">{tChat("loadingConfig")}</div>
                    ) : (
                      <>
                        {renderSelect(1)}
                        {renderSelect(2)}
                        {renderSelect(3)}
                      </>
                    )}
                  </div>

                  <div className="side-card">
                    <h4>{tChat("internalTitle")}</h4>
                    <div className="info-add-row">
                      <textarea
                        id="infoInput"
                        placeholder={tChat("internalPlaceholder")}
                        value={internalDraft}
                        onChange={(e) => setInternalDraft(e.target.value)}
                        disabled={detailLoading}
                      />
                      <button
                        type="button"
                        onClick={addInternalNote}
                        disabled={detailLoading}
                      >
                        {tChat("saveNote")}
                      </button>
                    </div>
                    <div className="info-list" id="infoList">
                      {internalNotes.length === 0 ? (
                        <div className="rename-note">{tChat("noInternal")}</div>
                      ) : (
                        internalNotes.map((note) => (
                          <div key={note.id} className="info-item">
                            <button
                              type="button"
                              className="remove-note"
                              onClick={() => removeNote(note.id)}
                              aria-label="Remover"
                            >
                              ×
                            </button>
                            <div className="info-date">{note.date}</div>
                            <div className="info-text">{note.text}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>
              </aside>
            ) : null}
          </div>
        </div>
      </section>

      {attachmentPreview.open ? (
        <div
          className="tp-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-label={tChat("attachmentPreviewTitle")}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) closeAttachmentPreview();
          }}
        >
          <div className="tp-modal">
            <div className="tp-modal-header">
              <div className="tp-modal-title">
                {tChat("attachmentPreviewTitle")}
              </div>
              <div className="tp-modal-actions">
                <button
                  type="button"
                  className="tp-modal-btn"
                  onClick={() => {
                    if (!attachmentPreview.messageId) {
                      toast.error(tChat("attachmentDownloadNotFound"));
                      return;
                    }
                    downloadMessageAttachment(
                      attachmentPreview.messageId,
                      attachmentPreview.filename
                    );
                  }}
                  disabled={attachmentPreview.loading}
                >
                  {tChat("attachmentDownload")}
                </button>
                <button
                  type="button"
                  className="tp-modal-btn tp-modal-btn--close"
                  onClick={closeAttachmentPreview}
                  aria-label={tChat("close")}
                >
                  ×
                </button>
              </div>
            </div>

            <div className="tp-modal-body">
              {attachmentPreview.loading ? (
                <div className="tp-modal-loading">{tChat("loading")}</div>
              ) : !attachmentPreview.url ? (
                <div className="tp-modal-loading">{tChat("attachmentPreviewUnavailable")}</div>
              ) : attachmentPreview.kind === "image" ? (
                <img
                  className="tp-preview-media"
                  src={attachmentPreview.url}
                  alt={attachmentPreview.filename || "anexo"}
                />
              ) : attachmentPreview.kind === "video" ? (
                <video className="tp-preview-media" src={attachmentPreview.url} controls />
              ) : attachmentPreview.kind === "pdf" ? (
                <iframe
                  className="tp-preview-frame"
                  title={attachmentPreview.filename || "PDF"}
                  src={attachmentPreview.url}
                />
              ) : (
                <div className="tp-modal-fallback">
                  <div className="tp-modal-filename">
                    {attachmentPreview.filename || "anexo"}
                  </div>
                  <div className="rename-note">{tChat("attachmentNoInlinePreview")}</div>
                  <button
                    type="button"
                    className="tp-modal-btn"
                    onClick={() => {
                      if (!attachmentPreview.messageId) {
                        toast.error(tChat("attachmentDownloadNotFound"));
                        return;
                      }
                      downloadMessageAttachment(
                        attachmentPreview.messageId,
                        attachmentPreview.filename
                      );
                    }}
                  >
                    {tChat("attachmentDownload")}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {filterModalOpen ? (
        <div
          className="tp-modal-backdrop"
          role="presentation"
          onClick={(e) => {
            if (e.target === e.currentTarget) setFilterModalOpen(false);
          }}
        >
          <div className="tp-modal tp-filter-modal" role="dialog" aria-modal="true">
            <div className="tp-modal-header">
              <div className="tp-modal-title">{tChat("filterTitle")}</div>
              <button
                type="button"
                className="tp-modal-btn tp-modal-btn--close"
                onClick={() => setFilterModalOpen(false)}
                aria-label={tChat("close")}
              >
                ×
              </button>
            </div>
            <div className="tp-modal-body tp-filter-modal-body">
              <div className="tp-filter-group">
                <div className="tp-filter-group-title">{tChat("filterType")}</div>
                <div className="tp-filter-check-list">
                  {SITUATION_FILTER_OPTIONS.map((opt) => {
                    const checked = filterDraft.situation.includes(opt.id);
                    return (
                      <label key={opt.id} className="tp-filter-check-item">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() =>
                            setFilterDraft((prev) => ({
                              ...prev,
                              situation: toggleFilterValue(prev.situation, opt.id),
                            }))
                          }
                        />
                        <span>{tChat(opt.labelKey)}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
              {renderFilterStageGroup("1")}
              {renderFilterStageGroup("2")}
              {renderFilterStageGroup("3")}
              {configLoading ? (
                <div className="rename-note">{tChat("loadingConfig")}</div>
              ) : null}
            </div>
            <div className="tp-filter-modal-footer">
              <button
                type="button"
                className="tp-modal-btn"
                onClick={clearFilterDraft}
              >
                {tChat("filterClear")}
              </button>
              <button
                type="button"
                className="tp-modal-btn tp-filter-apply-btn"
                onClick={applyListFilters}
              >
                {tChat("filterApply")}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {messageMenu ? (
        <div
          className="msg-context-menu"
          style={{ top: messageMenu.y, left: messageMenu.x }}
          role="menu"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            className="msg-context-menu-item"
            role="menuitem"
            onClick={() => startEditMessage(messageMenu.message)}
          >
            {tChat("messageEdit")}
          </button>
          <button
            type="button"
            className="msg-context-menu-item msg-context-menu-item--danger"
            role="menuitem"
            onClick={() => deleteMessage(messageMenu.message)}
          >
            {tChat("messageDelete")}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default TicketPublicoPage;
